export const MADRID_TZ = 'Europe/Madrid' as const;

const MADRID_DATE_TIME_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: MADRID_TZ,
  hour12: false,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

const MADRID_DATE_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: MADRID_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function partsToMap(parts: Intl.DateTimeFormatPart[]): Record<string, string> {
  return Object.fromEntries(parts.map((p) => [p.type, p.value])) as Record<string, string>;
}

/**
 * Returns the timezone offset (in minutes) between `date` (an instant) and the given IANA timeZone.
 * Positive means the zone is ahead of UTC.
 */
export function getTimeZoneOffsetMinutes(timeZone: string, date: Date): number {
  const fmt =
    timeZone === MADRID_TZ
      ? MADRID_DATE_TIME_FMT
      : new Intl.DateTimeFormat('en-US', {
          timeZone,
          hour12: false,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });

  const parts = fmt.formatToParts(date);
  const map = partsToMap(parts);

  const y = Number(map.year);
  const m = Number(map.month);
  const d = Number(map.day);
  const hh = Number(map.hour);
  const mm = Number(map.minute);
  const ss = Number(map.second);

  // Interpret the formatted "wall clock" parts as if they were UTC -> gives the zone's wallclock instant in UTC
  const asUtcMs = Date.UTC(y, m - 1, d, hh, mm, ss);

  // Difference between that and the original instant gives offset
  return Math.round((asUtcMs - date.getTime()) / 60000);
}

/**
 * Given a date-only string `YYYY-MM-DD`, returns the epoch ms for 00:00:00 of that date in Europe/Madrid.
 *
 * Works regardless of the browser timezone (Canarias, GMT, etc.)
 */
export function madridStartOfDayMs(dateIso: string): number {
  const [yS, mS, dS] = dateIso.split('-');
  const y = Number(yS);
  const m = Number(mS);
  const d = Number(dS);

  // Start by taking "UTC midnight" for that calendar date...
  const utcMidnight = new Date(Date.UTC(y, m - 1, d, 0, 0, 0));

  // ...then shift back by the Madrid offset at that instant to reach Madrid 00:00 for that date.
  const offsetMin = getTimeZoneOffsetMinutes(MADRID_TZ, utcMidnight);
  return utcMidnight.getTime() - offsetMin * 60_000;
}

export function msUntilMadridStartOfDay(dateIso: string, nowMs = Date.now()): number {
  return madridStartOfDayMs(dateIso) - nowMs;
}

/**
 * Formats milliseconds into a human-readable countdown string.
 *
 * - If >= 24 hours: returns format like "2d 20h"
 * - If < 24 hours and >= 1 minute: returns format like "HH:MM:SS"
 * - If < 1 minute: returns format like "HH:MM:SS.T" (with tenths of seconds)
 */
export function formatCountdown(milliseconds: number): string {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const tenths = Math.floor((milliseconds % 1000) / 100);

  // If >= 24 hours, show days and hours
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  }

  // If less than 1 minute, show tenths of seconds
  if (hours === 0 && minutes === 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${tenths}`;
  }

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Minutes since midnight in Madrid for a given instant (no synthetic shifted Date).
 */
export function getMadridMinutesSinceMidnight(instant: Date): number {
  const parts = MADRID_DATE_TIME_FMT.formatToParts(instant);
  const map = partsToMap(parts);

  const hh = Number(map.hour);
  const mm = Number(map.minute);
  return hh * 60 + mm;
}

/**
 * Converts a target Madrid wall-clock time (HH:mm) on the Madrid calendar date that corresponds
 * to `baseInstant` (plus addDays) into epoch ms.
 */
export function madridWallClockToEpochMs(
  baseInstant: Date,
  hour: number,
  minute: number,
  addDays = 0
): number {
  const parts = MADRID_DATE_FMT.formatToParts(baseInstant);
  const map = partsToMap(parts);

  const year = Number(map.year);
  const month = Number(map.month);
  const day = Number(map.day);

  const wallMs = Date.UTC(year, month - 1, day + addDays, hour, minute, 0, 0);

  const offsetMin = getTimeZoneOffsetMinutes(MADRID_TZ, new Date(wallMs));
  return wallMs - offsetMin * 60_000;
}